import { injectable } from '@servicetitan/react-ioc';
import { observable, runInAction, action, computed } from 'mobx';
import {
    SplitInfo,
    CreditCardInfo,
    ChargeCustomerCreditCardRequest,
    ChargeApiService,
    CustomerStatementModel,
    BankAccountInfo,
    ChargeCustomerACHRequest,
    ChargeType,
    CreditCardIssuer,
} from 'src/api';
import { FormState, FieldState } from 'formstate';
import * as Validators from 'src/invoice/components/card-info/cc-validators';
import { RouterStore } from 'src/core/stores/router.store';
import { ClientDataStore } from 'src/core/stores/client-data.store';
import { PaymentGateway, PGResponse, PaymentGatewayResponseType } from './payment-gateway.store';
import { HomeStore } from 'src/core/stores/home.store';
import { SavedCreditCardsStore } from 'src/payment-method/stores/saved-credit-cards.store';
import { ChargeCustomerStoredPaymentMethodRequest } from 'src/api/model/chargeCustomerStoredPaymentMethodRequest';

@injectable()
export class ChargeStore {
    @observable splits: SplitInfo[] = [];
    @observable outstandingBalance: number = 0;
    @observable statementBalance: number = 0;
    @observable paymentAmount: number = 0;
    @observable errorMessage?: string | any;
    @observable id?: string;
    @observable paymentCompleted: boolean = false;
    @observable authCode: string = '';
    @observable paymentProcessing: boolean = false;
    @observable remainingBalance: number = 0;
    @observable creditCardDisabled: boolean = false;
    @observable achDisabled: boolean = false;
    @observable multiplePaymentOptionsEnabled: boolean = false;
    @observable achShowAccountType: boolean = false;
    @observable achShowAccountHolderType: boolean = true;
    @observable needToStoreCreditCard: boolean = false;
    @observable useNewCard: boolean = false;

    constructor(
        private readonly chargeApiService: ChargeApiService,
        private readonly router: RouterStore,
        private readonly clientData: ClientDataStore,
        private readonly paymentGatewayService: PaymentGateway,
        private readonly savedCreditCardsStore: SavedCreditCardsStore,
        private readonly homeStore: HomeStore
    ) {}

    postalCode = new FieldState('').validators(Validators.zipPostalCodeValidator);

    form = new FormState({
        postalCode: this.postalCode,
    });

    @action
    initialize = (id: string) => {
        // if statement invoices have not been selected, redirect.
        if (this.paymentAmount <= 0) {
            this.router.history.replace(`/statement/${id}`);
        }

        this.id = id;
        this.postalCode.dirty = true;
        this.useNewCard = false;
        this.savedCreditCardsStore.setDefaultSelectedCardToPay();
    };

    initializeConfirmationPage = (id: string) => {
        if (this.paymentAmount <= 0) {
            this.router.history.replace(`/statement/${id}`);
        }
        this.id = id;

        this.homeStore.refreshHomeData(); // remove "Pay your balance" from SidenavWrapperImpl
        this.paymentCompleted = true; // will make StatementStore refresh its data
    };

    backToSelectionPage = (id: string) => {
        this.router.history.replace(`/statement/${id}`);
    };

    initializeFromStatementModel = (statementModel: CustomerStatementModel) => {
        if (statementModel.customers && statementModel.customers.length > 0) {
            this.creditCardDisabled = statementModel.customers[0].creditCardDisabled;
            this.achDisabled = statementModel.customers[0].achDisabled;
            this.multiplePaymentOptionsEnabled =
                statementModel.customers[0].multiplePaymentOptionsEnabled;
            this.achShowAccountType = statementModel.customers[0].achShowAccountType ?? false;
            this.achShowAccountHolderType = statementModel.customers[0].achShowAccountHolderType ?? true;
        }
    };

    @action
    clearError = () => {
        this.errorMessage = undefined;
    };

    convertToCardIssuer = (name: string | undefined): CreditCardIssuer => {
        if (name === undefined) {
            return CreditCardIssuer.NUMBER_0;
        }
        switch (name.toLowerCase()) {
            case 'americanexpress':
                return CreditCardIssuer.NUMBER_3;
            case 'visa':
                return CreditCardIssuer.NUMBER_4;
            case 'mastercard':
                return CreditCardIssuer.NUMBER_5;
            case 'discover':
                return CreditCardIssuer.NUMBER_6;
            default:
                return CreditCardIssuer.NUMBER_0;
        }
    };

    @action
    handleSubmit = async () => {
        this.paymentProcessing = true;
        if (this.isLoggedIn && this.savedCreditCardsStore.selectedCardToPay) {
            const request: ChargeCustomerStoredPaymentMethodRequest = {
                method: { id: this.savedCreditCardsStore.selectedCardToPay.id },
                amount: this.paymentAmount,
                customer: this.id!,
                chargeType: ChargeType.NUMBER_1,
                notificationEmail: this.clientData.loginData.email,
                splits: this.splits,
                storePaymentMethod: this.needToStoreCreditCard,
                isCreditCard: true
            };
            const response = await this.chargeApiService.chargeStoredPaymentMethod(request);
            runInAction(() => {
                this.paymentProcessing = false;
                this.remainingBalance = response.data.remainingBalance;
                this.authCode = response.data.transaction;
                this.paymentCompleted = true;
                this.router.history.replace(
                    `/statement/${this.id}/confirm-payment/${response.data.transaction}`
                );
            });
        } else {
            await this.form.validate();
            this.paymentGatewayService.handleSubmit(this.processCharge);
        }
    };

    @action
    processCharge = async (pgResponse: PGResponse) => {
        if (pgResponse.Type === PaymentGatewayResponseType.Error || this.form.hasError) {
            this.paymentProcessing = false;
            return;
        }
        const responseData = pgResponse.Data;
        const card: CreditCardInfo = {
            token: responseData.Token,
            billingZip: this.postalCode.$.trim(),
            number: responseData.Number,
            name: responseData.CardholderName,
            expMonth: responseData.ExpMonth,
            expYear: responseData.ExpYear,
            cvc: responseData.Cvc,
            issuerName: this.convertToCardIssuer(responseData.IssuerName),
        };

        const request: ChargeCustomerCreditCardRequest = {
            method: card,
            amount: this.paymentAmount,
            customer: this.id!,
            notificationEmail: this.clientData.loginData.email,
            splits: this.splits,
            storePaymentMethod: this.needToStoreCreditCard,
        };
        try {
            const response = await this.chargeApiService.chargeCreditCardMultiple(request);
            runInAction(() => {
                this.remainingBalance = response.data.remainingBalance;
                this.authCode = response.data.transaction;
                this.paymentCompleted = true;
                this.router.history.replace(
                    `/statement/${this.id}/confirm-payment/${response.data.transaction}`
                );
            });
        } catch (e) {
            runInAction(() => {
                this.errorMessage = e;
            });
        } finally {
            runInAction(() => {
                this.paymentProcessing = false;
            });
        }
    };

    @action
    processMultiBankAccount = async (bankAccount: BankAccountInfo, businessPayment: boolean) => {
        const request: ChargeCustomerACHRequest = {
            isBusinessPayment: businessPayment,
            chargeType: ChargeType.NUMBER_1,
            method: bankAccount,
            amount: this.paymentAmount,
            customer: this.id!,
            notificationEmail: this.clientData.loginData.email,
            splits: this.splits,
            storePaymentMethod: false,
        };
        try {
            const response = await this.chargeApiService.chargeBankAccount(request);
            runInAction(() => {
                this.remainingBalance = response.data.remainingBalance;
                this.authCode = response.data.transaction;
                this.paymentCompleted = true;
                this.router.history.replace(
                    `/statement/${this.id}/confirm-payment/${response.data.transaction}`
                );
            });
        } catch (e) {
            runInAction(() => {
                this.errorMessage = e;
            });
        } finally {
            runInAction(() => {
                this.paymentProcessing = false;
            });
        }
    };

    @action
    updateStoreCreditCardOption = (checked: boolean) => (this.needToStoreCreditCard = checked);

    @action
    setUseNewCard = (value: boolean) => (this.useNewCard = value);

    @computed
    get isLoggedIn() {
        return this.clientData.isLoggedIn && this.savedCreditCardsStore.isSavedCreditCardFeatureOn;
    }

    @computed get showLoginLink() {
        return !this.clientData.isLoggedIn && this.savedCreditCardsStore.isSavedCreditCardFeatureOn;
    }
}
